import React, {useEffect, useMemo, useState} from "react";
import {Autocomplete, Stack, TextField} from "@mui/material";
import {useDispatch, useSelector} from "react-redux";
import {usersActions} from "../../../../modules/users/users.actions.js";
import {filtreOperateursContratEnCours} from "../../../../utils/contratHelper";
import ContratsEnCoursToggle from "./ContratsEnCoursToggle";

const defaultGetOptionValue = (o) => o.id;
const AUCUN = {__aucun: true, libelle: "Aucun"};
const libelleOperateur = (o) => o?.libelle ?? o?.email ?? "";
const valeurOption = (o, getOptionValue) => (o === AUCUN ? "" : getOptionValue(o));

/**
 * Sélecteur d'opérateurs commun (B-001) : liste filtrable + bascule « Contrats en cours »
 * pré-activée. L'opérateur déjà sélectionné reste affiché même hors contrat.
 * `value` / `onChange` manipulent la valeur produite par `getOptionValue` (id ou @id).
 * Désélection (mode simple) : onChange("").
 */
const OperateurSelect = ({
    label,
    value,
    onChange,
    operateurs,
    getOptionValue = defaultGetOptionValue,
    multiple = false,
    required = false,
    error = false,
    helperText,
    disabled = false,
    allowNone = false,
}) => {
    const dispatch = useDispatch();
    const storeOperateurs = useSelector((state) => state.users?.operateurs);
    const loading = useSelector((state) => state.users?.loading);
    const [enCours, setEnCours] = useState(true);

    const external = operateurs !== undefined && operateurs !== null;
    useEffect(() => {
        if (!external && !storeOperateurs && !loading) {
            dispatch(usersActions.fetchOperateurs());
        }
    }, [external, storeOperateurs, loading, dispatch]);

    const source = external ? operateurs : storeOperateurs;
    const tous = useMemo(() => source ?? [], [source]);

    const selectedValues = useMemo(() => {
        if (multiple) return Array.isArray(value) ? value : [];
        return value === undefined || value === null || value === "" ? [] : [value];
    }, [value, multiple]);

    const selectedOptions = useMemo(
        () => selectedValues.map((v) => tous.find((o) => getOptionValue(o) === v)).filter(Boolean),
        [selectedValues, tous, getOptionValue]
    );

    const options = useMemo(() => {
        const base = enCours ? filtreOperateursContratEnCours(tous) : tous;
        const manquants = selectedOptions.filter((s) => !base.includes(s));
        const liste = [...manquants, ...base];
        return allowNone && !multiple ? [AUCUN, ...liste] : liste;
    }, [enCours, tous, selectedOptions, allowNone, multiple]);

    const isLoading = !external && loading && !storeOperateurs;

    return (
        <Stack spacing={0.5}>
            <Autocomplete
                multiple={multiple}
                options={options}
                loading={isLoading}
                loadingText="Chargement des données..."
                noOptionsText="Aucun opérateur"
                disabled={disabled}
                disableClearable={!allowNone && !multiple}
                value={multiple ? selectedOptions : (selectedOptions[0] ?? null)}
                getOptionLabel={libelleOperateur}
                isOptionEqualToValue={(a, b) => valeurOption(a, getOptionValue) === valeurOption(b, getOptionValue)}
                getOptionKey={(o) => valeurOption(o, getOptionValue)}
                onChange={(e, newValue) => {
                    if (multiple) onChange((newValue ?? []).map(getOptionValue));
                    else onChange(newValue ? valeurOption(newValue, getOptionValue) : "");
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={label}
                        required={required}
                        error={error}
                        helperText={helperText}
                    />
                )}
            />
            <ContratsEnCoursToggle value={enCours} onChange={setEnCours} disabled={disabled}/>
        </Stack>
    );
};

export default OperateurSelect;
